<script lang="ts">
    import { getContext, onDestroy } from 'svelte'
    import { derived as sderived } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import { DateTime } from 'luxon'

    import { gettext as _, ngettext } from 'hydria/services/gettext'

    import type { DomainHandler, IDomainResponse } from './domain'
    import type { IDomain } from 'wdom/domain/interfaces'
    import { simpleFetch } from 'hydria/services/session'
    import { notify } from 'hydria/services/notifications'

    import Date from 'hydria/components/Date.svelte'
    import { asAny, sanchor, spath } from 'hydria/svelte_helpers'

    const Domain = getContext('Domain') as DomainHandler
    const status = sderived(Domain.status, ($status) => $status)
    let domain: IDomain = $derived($status == 'ready' ? Domain.domain : <IDomain>{})

    // ===========================================================  Extend publishable date
    let newPubDate: string = $state('')
    let newPubDateIsValid = $derived(!!newPubDate.match(/^20[0-9]{2}-(0[1-9]|1[0-2])-[0-3][0-9]$/))
    let settingPubDate = $state(false)

    async function extendPublishableDate() {
        if (!newPubDateIsValid || settingPubDate) return

        settingPubDate = true
    }

    // ===========================================================  Permanent
    let settingPermanent = $state(false)
    async function setPermanent(nval: boolean) {
        settingPermanent = true
        try {
            const r = await simpleFetch(nval ? 'PUT' : 'DELETE', `/json/domain/1/zone/${Domain.idna}/permanent-pub`)
            Domain.loadResultDomainRelation(r.data as IDomainResponse)
            Domain.loadResultZoneStuff(r.data as IDomainResponse)
        } catch (e) {
            console.error('Failed to change zone permanence:', e)
            notify({
                type: 'danger',
                title: $_('Permanence de zone'),
                text: $_('Le changement n’a pas pu être effectué.'),
            })
        }

        settingPermanent = false
    }

    // =============================================================  Orders
    let showOrders = $state(false)

    $effect(() => {
        if ($status == 'ready' && showOrders && !document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.add('modal-open')
        } else if (!($status == 'ready' && showOrders) && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })

    onDestroy(() => {
        if (document.documentElement.classList.contains('modal-open') && showOrders) document.documentElement.classList.remove('modal-open')
    })
</script>

<style>
    .modal-zone-orders {
        --bs-modal-width: 70em;
    }
    .zone-order {
        display: grid;
        grid-template-rows: auto;
        grid-template-columns: 1fr 11em 11em 6em 22em;
        grid-gap: 1em;

        padding: 0.25em 1em;
    }
    .zone-order.zo-type-temporary {
        color: #a4660b;
    }
    .zone-order.zo-past {
        color: #aaa;
    }
    .zone-order.zo-past a {
        color: #7f959f;
    }
    .zone-order.zo-past.zo-type-temporary {
        color: #bb9f76;
    }

    .zone-order:not(:last-child) {
        border-bottom: 1px solid #bbb;
    }
    .zone-order.zo-head {
        font-weight: 500;
        border-bottom: 2px solid #bbb;
    }
    .zo-start,
    .zo-end {
        font-feature-settings: 'tnum' 1;
    }
    .zo-duration {
        justify-self: end;
    }
</style>

<form action="" class="d-grid">
    <div class="card">
        <div class="card-title text-primary">Propriétés de la zone</div>
        <div class="card-body">
            <div class="form-group row align-items-center">
                <label for="" class="col-form-label col-lg-5 bol-md-5 col-sm-5">Zone existe</label>
                <div class="col-lg-7 col-md-7 col-sm-6">
                    {#if !Domain.zoneDomainRelation.zone_exists}
                        <p class="form-control-plaintext">Non, <a use:sanchor href={spath(`/zone/${domain.owner}/new/${Domain.idna}`)}>créer</a></p>
                    {:else if !Domain.zoneDomainRelation.zone_published}
                        Oui, mais non-publiée.
                        <a use:sanchor href={spath(`/zone/${Domain.idna}`)}>éditer</a>
                    {:else}
                        Oui, et publiée.
                        <a use:sanchor href={spath(`/zone/${Domain.idna}`)}>éditer</a>
                    {/if}
                </div>
            </div>
            <div class="form-group row align-items-center">
                <label for="" class="col-form-label col-lg-5 col-md-5 col-sm-4">La zone est publiable</label>
                <div class="col-lg-7 col-md-7 col-sm-6">
                    <p class="form-control-plaintext">
                        {#if Domain.publishableZoneStatus.publishable}
                            Oui,
                            {#if Domain.publishableZoneStatus.permanent}<span>sans expiration</span>
                            {:else}<span>jusqu’au {asAny(Domain.publishableZoneStatus).end.toFormat('yyyy-MM-dd')}</span>
                                {#if Domain.publishableZoneStatus.is_extended}
                                    <span class="text-danger">(réel: Domain.publishableZoneStatus.real_expiration.toFormat('yyyy-MM-dd') })</span>
                                {/if}
                            {/if}
                        {:else}
                            Non
                        {/if}
                    </p>
                </div>
            </div>
            <div class="form-group row align-items-center">
                <label for="" class="col-form-label col-lg-5 col-md-5 col-sm-4">Pub: permanent</label>
                <div class="col-lg-7 col-md-7 col-sm-8">
                    {#if settingPermanent}
                        <button type="button" class="btn btn-secondary btn-sm" disabled
                            ><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                            …</button
                        >
                    {:else if Domain.publishableZoneStatus.permanent}
                        <button type="button" class="btn btn-secondary btn-sm" onclick={() => setPermanent(false)}>enlever permanence</button>
                    {:else}
                        <button type="button" class="btn btn-secondary btn-sm" onclick={() => setPermanent(true)}>rendre permanent</button>
                    {/if}
                </div>
            </div>
            {#if !Domain.publishableZoneStatus.permanent}
                <div class="form-group row align-items-center">
                    <label for="" class="col-form-label col-lg-5 col-md-5 col-sm-4">Pub: étendre</label>
                    <div class="col-lg-7 col-md-7 col-sm-8">
                        <div class="input-group">
                            <input type="text" placeholder="YYYY-MM-DD" class="form-control" bind:value={newPubDate} disabled={settingPubDate} />
                            <button class="btn btn-outline-secondary btn-sm" type="button" disabled={settingPubDate || !newPubDate || !newPubDateIsValid} onclick={() => extendPublishableDate()}>Étendre</button>
                        </div>
                    </div>
                </div>
            {/if}
            {#if Domain.publishableZoneStatus.orders != null && Domain.publishableZoneStatus.orders.length}
                <div class="form-group row align-items-center">
                    <label for="" class="col-form-label col-lg-5 col-md-5 col-sm-4">Achats</label>
                    <div class="col-lg-7 col-md-7 col-sm-8">
                        <button type="button" class="btn btn-secondary" onclick={() => (showOrders = true)}>Afficher</button>
                    </div>
                </div>
            {/if}
        </div>
    </div>
</form>

{#if $status == 'ready' && showOrders}
    <div class="modal modal-zone-orders" role="dialog" style="display: block" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header text-bg-primary">
                    <div>Zone : {Domain.unicode}</div>
                </div>

                <div class="modal-body">
                    <div class="zone-order-list">
                        <div class="zone-order zo-head">
                            <div class="zo-type">Type</div>
                            <div class="zo-start">Début</div>
                            <div class="zo-end">Fin</div>
                            <div class="zo-duration">Durée</div>
                            <div class="zo-order-id">Commande</div>
                        </div>
                        {#each asAny(Domain.publishableZoneStatus).orders as order}
                            <div class="zone-order zo-type-{order.type}" class:zo-past={order.end && order.end < DateTime.now()}>
                                <span class="zo-type">
                                    {#if order.type == 'manual'}
                                        Achat « custom »
                                    {:else if order.type == 'temporary'}
                                        Extension temporaire
                                    {:else if order.type == 'paid'}
                                        {#if order.source == 'manual'}
                                            Extension « achat »
                                        {:else}
                                            Achat
                                        {/if}
                                    {:else}
                                        ???
                                    {/if}
                                </span>
                                <span class="zo-start"><Date value={order.pub_start} format="h-table-s" /></span>
                                <span class="zo-end"><Date value={order.pub_end} format="h-table-s" /></span>
                                <span class="zo-duration">
                                    {#if order.n_days != null}{$ngettext('{n} jour', '{n} jours', { n: order.n_days })}{/if}
                                    {#if order.n_years != null}{$ngettext('{n} an', '{n} ans', { n: order.n_years })}{/if}
                                </span>
                                <span class="zo-order-id">
                                    {#if order.order_id}
                                        <a use:sanchor href={spath(`/ecom/order/${order.order_id}`)}>{order.order_id}</a>
                                    {/if}
                                </span>
                            </div>
                        {/each}
                    </div>
                </div>

                <div class="modal-footer text-bg-light">
                    <button class="btn btn-secondary" type="button" onclick={() => (showOrders = false)}>{$_('Cancel')}</button>
                </div>
            </div>
        </div>
        <div
            class="modal-backdrop"
            onclick={() => {
                showOrders = false
            }}
            aria-hidden="true"
        ></div>
    </div>
{/if}
